/**
 * Settings → "Sign-in methods": the ways I can sign in to this account (Apple, Google, email codes), each with the
 * address it uses; "Link Apple" / "Link Google" (web: a redirect that comes back here; native: the platform SDK);
 * "Add email sign-in" (a code sent to a new address, which becomes the account's email); "Remove" while another
 * method remains (`logic/signInMethods`); and "Use Google photo". On desktop, linking happens in the browser like
 * sign-in: the panel lists the methods and offers "Manage in browser". Online only.
 */
import type { AuthMethodsResponse, LinkedProvider, SignInMethodsResponse } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { isCompleteEmailCode, isValidEmail, normalizeEmailCode } from "../logic/auth.ts";
import { linkErrorFor, linkOptions, signInMethodList, type LinkError, type LinkOptions, type SignInMethod } from "../logic/signInMethods.ts";
import { ApiFailure, errorKind, type AccountLinking, type ApiClient } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";

export type SignInMethodsDeps = {
  linking: AccountLinking;
  /** What the deployment offers (GET /v1/auth/methods), for the "Link …" buttons. */
  api: Pick<ApiClient, "authMethods">;
  /** Where a web redirect comes back to (the settings page). */
  returnTo: string;
};

export type SignInMethodsBusy = "apple" | "google" | "unlink" | "sendingCode" | "verifying" | "photo" | "browser";

/** What a web redirect brought back (`?linked=<provider>` or `?linkError=<provider>&error=<code>`). */
export type LinkReturn = { provider: LinkedProvider; error?: string };

export type SignInMethodsState = {
  status: "loading" | "ready" | "error";
  /** Apple, Google, then email codes. */
  methods: SignInMethod[];
  /** What can be added. */
  link: LinkOptions;
  /** Desktop: linking happens in the browser ("Manage in browser"); nothing else is actionable here. */
  inBrowser: boolean;
  busy?: SignInMethodsBusy;
  /** "Remove … sign-in?" is open for this method. */
  confirming?: SignInMethod;
  /** "Add email sign-in": entering the address, then the code sent to it. */
  addEmail?: { step: "email" | "code"; email: string };
  error?: MessageDescriptor;
  notice?: MessageDescriptor;
};

const NO_LINKS: LinkOptions = { apple: false, google: false, email: false };

function errorMessage(error: LinkError): MessageDescriptor {
  switch (error) {
    case "otherAccount":
      return msg("signInMethods.error.otherAccount");
    case "alreadyLinked":
      return msg("signInMethods.error.alreadyLinked");
    case "lastMethod":
      return msg("signInMethods.error.lastMethod");
    case "signInAgain":
      return msg("signInMethods.error.signInAgain");
    case "emailInUse":
      return msg("signInMethods.error.emailInUse");
    case "invalidEmail":
      return msg("auth.error.invalidEmail");
    case "invalidCode":
      return msg("auth.error.invalidCode");
    case "rateLimited":
      return msg("auth.error.rateLimited");
    case "cancelled":
      return msg("signInMethods.error.cancelled");
    case "network":
      return msg("auth.error.network");
    case "failed":
      return msg("signInMethods.error.failed");
  }
}

/** A rejected call → its message (offline first, then the server's code). */
function failureMessage(e: unknown): MessageDescriptor {
  if (errorKind(e) === "network") return errorMessage("network");
  if (e instanceof ApiFailure) {
    const known = linkErrorFor(e.code);
    if (known !== "failed") return errorMessage(known);
    if (e.kind === "rateLimited") return errorMessage("rateLimited");
  }
  return errorMessage("failed");
}

function linkedNotice(provider: LinkedProvider): MessageDescriptor {
  return provider === "apple" ? msg("signInMethods.linked.apple") : msg("signInMethods.linked.google");
}

function removedNotice(kind: SignInMethod["kind"]): MessageDescriptor {
  return kind === "apple" ? msg("signInMethods.removed.apple") : msg("signInMethods.removed.google");
}

export class SignInMethodsViewModel extends ViewModel<SignInMethodsState> {
  private readonly deps: SignInMethodsDeps;
  private offered: AuthMethodsResponse | undefined;
  private data: SignInMethodsResponse | undefined;

  constructor(deps: SignInMethodsDeps) {
    super({ status: "loading", methods: [], link: NO_LINKS, inBrowser: deps.linking.openInBrowser !== undefined });
    this.deps = deps;
  }

  /** Loads the methods (again after a failure). `returned`: what a web redirect brought back, shown once. */
  async load(returned?: LinkReturn): Promise<void> {
    if (returned !== undefined) {
      if (returned.error === undefined) this.update({ notice: linkedNotice(returned.provider), error: undefined });
      else this.update({ error: errorMessage(linkErrorFor(returned.error)), notice: undefined });
    }
    if (this.state.status === "error") this.update({ status: "loading" });
    try {
      const [data, offered] = await Promise.all([this.deps.linking.list(), this.offered ?? this.deps.api.authMethods()]);
      if (this.isDisposed) return;
      this.offered = offered;
      this.apply(data);
    } catch {
      if (!this.isDisposed && this.data === undefined) this.update({ status: "error" });
    }
  }

  private apply(data: SignInMethodsResponse): void {
    this.data = data;
    const link = this.state.inBrowser || this.offered === undefined ? NO_LINKS : linkOptions(data, this.offered);
    this.update({ status: "ready", methods: signInMethodList(data), link });
  }

  private async reload(): Promise<void> {
    try {
      const data = await this.deps.linking.list();
      if (!this.isDisposed) this.apply(data);
    } catch {
      // Keep the last list; the next action or visit refreshes it.
    }
  }

  /** "Link Apple" / "Link Google". Web leaves the page; native finishes here. */
  async link(provider: LinkedProvider): Promise<void> {
    if (this.state.busy !== undefined || this.state.inBrowser || !this.state.link[provider]) return;
    this.update({ busy: provider, error: undefined, notice: undefined });
    try {
      const result = await this.deps.linking.link(provider, this.deps.returnTo);
      if (this.isDisposed || result === "redirected") return;
      await this.reload();
      this.update({ busy: undefined, notice: linkedNotice(provider) });
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: undefined, error: failureMessage(e) });
    }
  }

  /** "Remove": asks first (only for methods that can go). */
  askUnlink(accountId: string): void {
    const method = this.state.methods.find((m) => m.accountId === accountId);
    if (method === undefined || this.state.busy !== undefined || this.state.inBrowser) return;
    if (!method.canUnlink) {
      this.update({ error: errorMessage("lastMethod"), notice: undefined });
      return;
    }
    this.update({ confirming: method, error: undefined, notice: undefined });
  }

  cancelUnlink(): void {
    if (this.state.busy === "unlink") return;
    this.update({ confirming: undefined });
  }

  async confirmUnlink(): Promise<void> {
    const method = this.state.confirming;
    if (method?.accountId === undefined || this.state.busy !== undefined) return;
    this.update({ busy: "unlink", error: undefined });
    try {
      await this.deps.linking.unlink(method.accountId);
      if (this.isDisposed) return;
      await this.reload();
      this.update({ busy: undefined, confirming: undefined, notice: removedNotice(method.kind) });
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: undefined, confirming: undefined, error: failureMessage(e) });
    }
  }

  // ---------- Add email sign-in ----------

  startAddEmail(): void {
    if (!this.state.link.email || this.state.busy !== undefined) return;
    this.update({ addEmail: { step: "email", email: "" }, error: undefined, notice: undefined });
  }

  cancelAddEmail(): void {
    if (this.state.busy === "sendingCode" || this.state.busy === "verifying") return;
    this.update({ addEmail: undefined, error: undefined });
  }

  /** Back from the code step to change the address. */
  editEmail(): void {
    const form = this.state.addEmail;
    if (form !== undefined) this.update({ addEmail: { step: "email", email: form.email }, error: undefined });
  }

  async sendEmailCode(email: string): Promise<void> {
    const address = email.trim().toLowerCase();
    if (this.state.addEmail === undefined || this.state.busy !== undefined) return;
    if (!isValidEmail(address)) {
      this.update({ error: errorMessage("invalidEmail") });
      return;
    }
    this.update({ busy: "sendingCode", error: undefined });
    try {
      await this.deps.linking.sendEmailCode(address);
      if (!this.isDisposed) this.update({ busy: undefined, addEmail: { step: "code", email: address } });
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: undefined, error: failureMessage(e) });
    }
  }

  resendEmailCode(): Promise<void> {
    const form = this.state.addEmail;
    return form?.step === "code" ? this.sendEmailCode(form.email) : Promise.resolve();
  }

  async verifyEmailCode(code: string): Promise<void> {
    const form = this.state.addEmail;
    const digits = normalizeEmailCode(code);
    if (form?.step !== "code" || this.state.busy !== undefined) return;
    if (!isCompleteEmailCode(digits)) {
      this.update({ error: errorMessage("invalidCode") });
      return;
    }
    this.update({ busy: "verifying", error: undefined });
    try {
      await this.deps.linking.verifyEmailCode(form.email, digits);
      if (this.isDisposed) return;
      await this.reload();
      this.update({ busy: undefined, addEmail: undefined, notice: msg("signInMethods.emailAdded", { email: form.email }) });
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: undefined, error: failureMessage(e) });
    }
  }

  // ---------- Photo ----------

  /** "Use Google photo": it replaces my profile photo in every workspace. */
  async applyProviderPhoto(accountId: string): Promise<void> {
    if (this.state.busy !== undefined || this.state.inBrowser) return;
    this.update({ busy: "photo", error: undefined, notice: undefined });
    try {
      await this.deps.linking.applyProviderPhoto(accountId);
      if (!this.isDisposed) this.update({ busy: undefined, notice: msg("signInMethods.photoUsed") });
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: undefined, error: failureMessage(e) });
    }
  }

  // ---------- Desktop ----------

  /** "Manage in browser" (desktop): <web>/settings/profile in the system browser. */
  async openInBrowser(): Promise<void> {
    const open = this.deps.linking.openInBrowser;
    if (open === undefined || this.state.busy !== undefined) return;
    this.update({ busy: "browser", error: undefined });
    try {
      await open.call(this.deps.linking);
      if (!this.isDisposed) this.update({ busy: undefined });
    } catch {
      if (!this.isDisposed) this.update({ busy: undefined, error: msg("auth.error.browser") });
    }
  }

  dismissNotice(): void {
    this.update({ notice: undefined, error: undefined });
  }
}
